import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ResearchProject } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ResearchProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getProjects();
      if (response.success && response.data) {
        setProjects(response.data);
      } else {
        setError(response.error || 'Failed to load projects');
        setProjects([]);
      }
    } catch (error: any) {
      console.error('Failed to load projects:', error);
      setError(error.message || 'Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusSeverity = (status: string) => {
    switch (status) {
      case 'complete': return 'success';
      case 'data_collection': return 'info';
      case 'kobo_deployed': return 'warning';
      case 'draft': return 'secondary';
      default: return 'info';
    }
  };

  const statusBodyTemplate = (rowData: ResearchProject) => (
    <Tag value={rowData.status.replace('_', ' ').toUpperCase()} severity={getStatusSeverity(rowData.status)} />
  );

  const actionBodyTemplate = (rowData: ResearchProject) => (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <Button icon="pi pi-eye" size="small" text onClick={() => navigate(`/app/projects/${rowData.projectId}`)} tooltip="View" />
      <Button icon="pi pi-pencil" size="small" text onClick={() => navigate(`/app/projects/${rowData.projectId}/edit`)} tooltip="Edit" />
    </div>
  );

  const dateBodyTemplate = (rowData: ResearchProject) => new Date(rowData.createdAt).toLocaleDateString();

  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };

  const stats = [
    { label: 'Total Projects', value: projects.length, icon: 'pi pi-folder', color: '#3B82F6', bg: '#dbeafe' },
    { label: 'Active Projects', value: projects.filter(p => p.status !== 'complete' && p.status !== 'draft').length, icon: 'pi pi-play', color: '#22C55E', bg: '#dcfce7' },
    { label: 'Data Collection', value: projects.filter(p => p.status === 'data_collection').length, icon: 'pi pi-chart-bar', color: '#F97316', bg: '#ffedd5' },
    { label: 'Completed', value: projects.filter(p => p.status === 'complete').length, icon: 'pi pi-check-circle', color: '#A855F7', bg: '#f3e8ff' }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#3B82F6' }}></i>
      </div>
    );
  }

  return (
    <div>
      {/* Header Section */}
      <div style={{ padding: '2rem', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>Welcome back, {user?.name}</h1>
            <p style={{ color: '#6b7280', margin: 0 }}>Manage your research projects and automation workflows</p>
          </div>
          <Button label="New Project" icon="pi pi-plus" onClick={() => navigate('/app/projects/new')} />
        </div>
      </div>

      {/* Stats Section */}
      <div style={{ padding: '2rem', backgroundColor: '#f8f9fa', width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          {stats.map((stat, i) => (
            <div key={i} style={{ ...cardStyle, borderLeft: `4px solid ${stat.color}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: stat.color, fontWeight: 500, marginBottom: '0.25rem' }}>{stat.label}</div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937' }}>{stat.value}</div>
                </div>
                <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: stat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={stat.icon} style={{ color: stat.color, fontSize: '1.25rem' }}></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Projects Section */}
      <div style={{ padding: '2rem', backgroundColor: 'white', minHeight: 'calc(100vh - 280px)', width: '100%' }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', margin: 0, color: '#1f2937' }}>Recent Projects</h2>
            <Button label="View All" text onClick={() => navigate('/app/projects')} />
          </div>

          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="pi pi-exclamation-triangle" style={{ color: '#dc2626' }}></i>
                <span style={{ color: '#dc2626', fontWeight: 500 }}>Error loading projects</span>
              </div>
              <p style={{ color: '#7f1d1d', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
                {error}
              </p>
              <Button 
                label="Retry" 
                size="small" 
                text 
                onClick={loadProjects}
                style={{ marginTop: '0.5rem' }}
              />
            </div>
          )}

          {!error && projects.length === 0 && !loading && (
            <div style={{
              textAlign: 'center',
              padding: '3rem 1rem',
              color: '#6b7280'
            }}>
              <i className="pi pi-folder-open" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}></i>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>No projects yet</h3>
              <p style={{ margin: '0 0 1.5rem 0' }}>Get started by creating your first research project</p>
              <Button label="Create Project" icon="pi pi-plus" onClick={() => navigate('/app/projects/new')} />
            </div>
          )}

          {projects.length > 0 && (
            <DataTable value={projects.slice(0, 5)} responsiveLayout="scroll" className="p-datatable-sm">
              <Column field="title" header="Project Title" />
              <Column field="status" header="Status" body={statusBodyTemplate} />
              <Column field="createdAt" header="Created" body={dateBodyTemplate} />
              <Column body={actionBodyTemplate} header="Actions" style={{ width: '8rem' }} />
            </DataTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
